import type {
  SessionSubTypeConfig,
  SportsConfig
} from '@frontend/core/lib/domains/sports/config'
import { sportsConfig } from '@frontend/core/lib/domains/sports/config'
import { selectIsNotificationActive } from '@frontend/core/lib/modules/notifications'
import _ from 'lodash'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { SessionContext } from '../domains'
import { useAppSelector } from '../store/hooks'

// Sentry mounts the feedback form inside a shadow root hosted by #sentry-feedback.
// composedPath() gives us the full event path across that boundary, so we can
// still detect that the key event came from the Sentry feedback form.
export const isSentryFeedbackHotkeyTarget = (event: KeyboardEvent) =>
  event
    .composedPath()
    .some(
      (node) => node instanceof HTMLElement && node.id === 'sentry-feedback'
    )

// KeyboardEvent.key values for modifier keys — never valid parts of a recorded
// jersey number, so the recorder skips them (see useHotKeyRecorder).
const MODIFIER_KEYS = ['Shift', 'Control', 'Alt', 'Meta']

export type HotKeyConfig = {
  shortcut: string
  name: string
  description: string
  value?: string //TODO: can change this if necessary
}

export type HotKeyConfigMap = Record<HotKey, HotKeyConfig[]>

export const createConfig = (
  sessionEventConfig: SessionSubTypeConfig,
  sportConfig: SportsConfig
): HotKeyConfigMap => {
  // Extract Outcomes, Game and Play Events
  const sportDataTypes =
    sessionEventConfig.hierarchyLevels?.SportEvent?.dataTypes
  const playDataTypes = sessionEventConfig.hierarchyLevels?.Play?.dataTypes

  const outcomes: HotKeyConfig[] = []
  const gameEvents: HotKeyConfig[] = []
  const playEvents: HotKeyConfig[] = []

  const baseShortcuts = sportConfig.shortcuts

  const playHierarchyEnabled = !!playDataTypes

  if (playHierarchyEnabled) {
    Object.values(playDataTypes).forEach((dataTypeEventConfig) => {
      const types = dataTypeEventConfig.types

      for (const dataType of Object.values(types)) {
        const { shortcut, description, enableManualCoding } = dataType.features

        if (shortcut && enableManualCoding) {
          playEvents.push({
            name: dataType.name.en,
            description,
            shortcut,
            value: dataType.value
          })
        }
      }
    })
  }

  if (sportDataTypes) {
    Object.values(sportDataTypes).forEach((dataTypeEventConfig) => {
      const types = dataTypeEventConfig.types

      for (const dataType of Object.values(types)) {
        // Aggregate outcomes
        for (const outcome of Object.keys(dataType.outcomes)) {
          const outcomeConfig = dataType.outcomes[outcome]
          if (outcomeConfig.shortcut) {
            outcomes.push({
              name: outcome,
              shortcut: outcomeConfig.shortcut,
              description: outcomeConfig.description ?? '',
              value: outcomeConfig.value
            })
          }
        }

        // Generate game event config
        const { enableManualCoding, description, shortcut } = dataType.features

        if (enableManualCoding && shortcut) {
          gameEvents.push({
            name: dataType.name.en,
            description,
            shortcut,
            value: dataType.value
          })
        }
      }
    })
  }

  // Note: base shortcuts are added below
  const config: Partial<HotKeyConfigMap> = {
    [HotKey.PLAY_EVENTS]: playEvents,
    [HotKey.GAME_EVENTS]: gameEvents,
    [HotKey.OUTCOMES]: outcomes
  }

  // confirm all hotkeys declared in config are registered
  const unregisteredShortcuts = []

  for (const key of Object.keys(baseShortcuts)) {
    const shortcutConfig = baseShortcuts[key]

    // omit if not in list of hotkeys or if config doesn't specify a key
    if (!isHotKey(key) || !shortcutConfig?.shortcut) {
      unregisteredShortcuts.push(shortcutConfig)
      continue
    }

    config[key] = [shortcutConfig]
  }

  if (unregisteredShortcuts.length > 0) {
    console.error(
      'unregistered shortcuts declared in config',
      unregisteredShortcuts
    )
  }

  // confirm all hotkeys specified in the enum have corresponding shortcuts in the config
  const unregisteredShortcutsInEnum = []

  for (const key of AllHotKeys) {
    if (NonBaseShortcutHotKeys.includes(key as any)) {
      continue
    }

    if (!baseShortcuts[key]) {
      unregisteredShortcutsInEnum.push(key)
    }
  }

  if (unregisteredShortcutsInEnum.length > 0) {
    console.error(
      'unregistered shortcuts missing from config',
      unregisteredShortcutsInEnum
    )
  }

  return config as HotKeyConfigMap
}

export enum HotKey {
  PLAY_EVENTS = 'playEvents',
  GAME_EVENTS = 'gameEvents',
  OUTCOMES = 'outcomes',

  SUBMIT_PLAY = 'submitPlay',

  POSSESSION_LEFT = 'possessionLeft',
  POSSESSION_RIGHT = 'possessionRight',

  IGNORE = 'ignore',
  HIGHLIGHT = 'highlight',

  ASSIGN_PLAYER = 'assignPlayer',
  ASSIGN_RECIEVER = 'assignReceiver',
  ASSIGN_TARGET = 'assignTarget',
  ASSIGN_TEAM = 'assignTeam',

  UNASSIGN_PLAYER = 'unassignPlayer',
  UNASSIGN_RECEIVER = 'unassignReceiver',
  UNASSIGN_TARGET = 'unassignTarget'
}

export const AllHotKeys = Object.values(HotKey)

const isHotKey = (value: string): value is HotKey =>
  AllHotKeys.includes(value as HotKey)

export const NonBaseShortcutHotKeys = [
  HotKey.PLAY_EVENTS,
  HotKey.GAME_EVENTS,
  HotKey.OUTCOMES
] as const

export const useHotkeyConfig = (
  sessionEventConfig: SessionSubTypeConfig | undefined
) => {
  const config = useMemo(() => {
    if (sessionEventConfig)
      return createConfig(sessionEventConfig, sportsConfig)

    return null
  }, [sessionEventConfig])

  return {
    config
  }
}

export const useHotkey = (
  hotkey: HotKey,
  cb: (value?: string) => void,
  isActive = true,
  throttleTimeInMs = 500
) => {
  const { hotKeyConfig: config } = useContext(SessionContext)
  const isModalActive = useAppSelector((state) => state.modal.active)
  const isNotificationActive = useAppSelector(selectIsNotificationActive)

  const throttledCbRef = useRef<_.DebouncedFunc<typeof cb> | null>(null)

  useEffect(() => {
    const throttled = _.throttle(cb, throttleTimeInMs, {
      leading: true,
      trailing: false
    })

    throttledCbRef.current = throttled

    return () => {
      throttled.cancel()
    }
  }, [cb, throttleTimeInMs])

  const configsForKey = useMemo(() => {
    return config?.[hotkey] || []
  }, [config, hotkey])

  const { valueMap, keys } = useMemo(() => {
    const valueMap = new Map<string, string | undefined>()
    const keys: string[] = []

    for (const { shortcut, value } of configsForKey) {
      // react-hotkeys-hook matches bindings on e.code, which is caps-lock
      // immune, but the value lookup below uses e.key — lowercase both sides
      // so caps lock or upper-case config entries can't break resolution.
      const normalizedShortcut = shortcut.toLowerCase()

      if (keys.includes(normalizedShortcut)) {
        console.warn('note skipping shortcut: ', shortcut, 'for hotkey', hotkey)
        continue
      }

      keys.push(normalizedShortcut)
      valueMap.set(normalizedShortcut, value)
    }

    return {
      valueMap,
      keys
    }
  }, [configsForKey])

  const handler = useCallback(
    async (e: KeyboardEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (e.repeat) return

      //TODO: can add a custom key mapping layer if this grows beyond just space
      const key = e.key === ' ' ? 'space' : e.key.toLowerCase()
      const value = valueMap.get(key)

      if (throttledCbRef.current) {
        throttledCbRef.current(value)
      } else {
        console.warn(
          'No throttled callback set for hotkey',
          hotkey,
          'on key press',
          key,
          'with value',
          value
        )
      }
    },
    [valueMap]
  )

  const ref = useHotkeys(
    keys,
    handler,
    {
      enabled: isActive && !isModalActive && !isNotificationActive,
      ignoreEventWhen: isSentryFeedbackHotkeyTarget
    },
    [handler, keys]
  )

  return {
    ref
  }
}

// The recorder flow works by specifiying the hotkey which triggers recording
// once that's pressed it will record all other keys pressed until the specified hotkey is released
// and will then pass the combined keys as a concatenated string to the callback
export const useHotKeyRecorder = (
  hotkey: HotKey,
  cb: (value?: string) => void,
  isActive = true
) => {
  const { hotKeyConfig: config } = useContext(SessionContext)
  const isModalActive = useAppSelector((state) => state.modal?.active || false)
  const isNotificationActive = useAppSelector(selectIsNotificationActive)

  const [keysPressed, setKeysPressed] = useState<string[]>([])
  const [isRecording, setIsRecording] = useState(false)

  const configForKey = useMemo(() => {
    const configsForKey = config?.[hotkey] || []

    if (configsForKey?.length === 0) {
      console.error('no config for hotkey in recorder', hotkey, { config })

      return null
    }

    if (configsForKey.length > 1) {
      console.warn(
        'recorder hotkey flow expects a single config for key',
        hotkey,
        'got multiple',
        configsForKey
      )
    }

    return configsForKey[0]
  }, [config, hotkey])

  const isHotkeyEnabled =
    isActive &&
    !!configForKey?.shortcut &&
    !isModalActive &&
    !isNotificationActive

  useHotkeys(
    configForKey?.shortcut ?? [],
    (e) => {
      setKeysPressed([])
      setIsRecording(true)
    },
    {
      keydown: true,
      enabled: isHotkeyEnabled,
      ignoreEventWhen: isSentryFeedbackHotkeyTarget
    }
  )

  useHotkeys(
    configForKey?.shortcut ?? [],
    (e) => {
      setIsRecording(false)
      cb(keysPressed.join(''))
      setKeysPressed([])
    },
    {
      keyup: true,
      enabled: isHotkeyEnabled,
      ignoreEventWhen: isSentryFeedbackHotkeyTarget
    }
  )

  useHotkeys(
    '*',
    (e) => {
      if (e.repeat) return

      const key = e.key
      // The '*' binding bypasses react-hotkeys-hook's modifier matching, so a
      // modifier keydown (e.g. pressing Shift while holding P for Shift+P) would
      // otherwise be recorded as part of the number. Skip modifier keys so the
      // unassign combos never corrupt an in-flight assign recording.
      if (MODIFIER_KEYS.includes(key)) return

      setKeysPressed((keys) => [...keys, key])
    },
    {
      enabled: isRecording && isHotkeyEnabled,
      ignoreEventWhen: isSentryFeedbackHotkeyTarget
    }
  )

  return {
    keysPressed,
    isRecording
  }
}
